import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Check, Loader2, Pencil, AlertTriangle } from "lucide-react";

/** Controlled inline editor. Same backend validation/RBAC as forms via
 *  PATCH /inline/{etype}/{eid}. Optimistic-concurrency: 409 → conflict UI. */
export const InlineEditField = ({ etype, eid, field, spec, value, version, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const [status, setStatus] = useState("idle"); // idle|saving|saved|error
  const [conflict, setConflict] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { setVal(value ?? ""); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);
  useEffect(() => {
    if (status !== "saved") return;
    const t = setTimeout(() => setStatus("idle"), 2500);
    return () => clearTimeout(t);
  }, [status]);

  const save = async (baseVersion = version) => {
    if (String(val ?? "") === String(value ?? "")) { setEditing(false); return; }
    setStatus("saving");
    try {
      const { data } = await api.patch(`/inline/${etype}/${eid}`, {
        field, value: val === "" ? null : val, base_version: baseVersion,
      });
      setStatus("saved");
      setEditing(false);
      setConflict(null);
      onSaved?.(field, data.version, val);
    } catch (e) {
      if (e.response?.status === 409) {
        setConflict(e.response.data.detail);
        setStatus("idle");
      } else {
        setStatus("error");
      }
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && spec.type !== "textarea") { e.preventDefault(); save(); }
    if (e.key === "Escape") { setVal(value ?? ""); setEditing(false); setConflict(null); }
  };

  if (conflict) {
    return (
      <div className="border border-amber-300 bg-amber-50 rounded-md p-2 text-[12px] space-y-1.5"
        data-testid={`inline-conflict-${field}`}>
        <div className="flex items-center gap-1.5 font-medium text-amber-800">
          <AlertTriangle className="h-3.5 w-3.5" /> This record has changed
        </div>
        <div className="text-slate-700">Current value: <span className="font-medium">{String(conflict.current_value ?? "—")}</span></div>
        <div className="text-slate-700">Your value: <span className="font-medium">{String(val || "—")}</span></div>
        <div className="flex gap-2 pt-0.5">
          <button data-testid={`inline-conflict-theirs-${field}`}
            onClick={() => { setVal(conflict.current_value ?? ""); setConflict(null); setEditing(false); onSaved?.(field, conflict.current_version, conflict.current_value); }}
            className="h-6 px-2 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
            Keep theirs
          </button>
          <button data-testid={`inline-conflict-mine-${field}`}
            onClick={() => save(conflict.current_version)}
            className="h-6 px-2 rounded bg-slate-900 text-white hover:bg-slate-800">
            Apply mine
          </button>
        </div>
      </div>
    );
  }

  if (!editing) {
    return (
      <button type="button" onClick={() => setEditing(true)}
        data-testid={`inline-edit-${field}`}
        className="group text-left w-full flex items-start gap-1.5 rounded px-1 -mx-1 py-0.5 hover:bg-amber-50/70 transition-colors">
        <span className={`min-w-0 break-words ${value == null || value === "" ? "text-slate-300 italic" : "text-slate-800"}`}>
          {value == null || value === "" ? "Add…"
            : spec.type === "number"
              ? `₹ ${Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
              : String(value)}
        </span>
        <span className="ml-auto shrink-0 flex items-center gap-1">
          {status === "saved" && (
            <span className="text-[10px] text-emerald-600 inline-flex items-center gap-0.5" data-testid={`inline-saved-${field}`}>
              <Check className="h-3 w-3" /> Saved
            </span>
          )}
          {status === "error" && (
            <span onClick={(e) => { e.stopPropagation(); save(); }}
              className="text-[10px] text-red-600 cursor-pointer" data-testid={`inline-retry-${field}`}>
              Save failed – Retry
            </span>
          )}
          <Pencil className="h-3 w-3 text-slate-300 opacity-0 group-hover:opacity-100" />
        </span>
      </button>
    );
  }

  const common = {
    ref: inputRef, value: val, onKeyDown: onKey,
    onChange: (e) => setVal(e.target.value),
    onBlur: () => save(),
    "data-testid": `inline-input-${field}`,
    className: "w-full border border-amber-300 rounded px-1.5 py-1 text-sm text-slate-900 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400",
  };

  if (spec.type === "select") {
    return (
      <div className="w-full flex items-center gap-1.5">
        <select {...common} onChange={(e) => setVal(e.target.value)}>
          {(spec.options || []).map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
        </select>
        {status === "saving" && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400 shrink-0" />}
      </div>
    );
  }

  return (
    <div className="w-full flex items-center gap-1.5">
      {spec.type === "textarea"
        ? <textarea rows={3} {...common} />
        : <input type={spec.type === "number" ? "number" : spec.type === "date" ? "date" : "text"} {...common} />}
      {status === "saving" && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400 shrink-0" />}
    </div>
  );
};

export default InlineEditField;
